// SEO + Ads Monitor — /seo/monitor
//
// Per-site SEO health check + social/sharing preview + Google verification
// + Google Ads/AdSense integration + ad-slot placement. Reads/writes
// websites.seo_config and websites.monetization_config.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, CheckCircle2, AlertCircle, Copy, ExternalLink, Globe, Image as ImageIcon,
  Loader2, Search, Shield, Twitter, Facebook, Linkedin, FileText, MapPin, Megaphone,
  Eye, Info,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "@/hooks/useCompany";
import { supabase } from "@/integrations/supabase/client";
import {
  AD_SLOTS,
  AdSlotKey,
  buildSitemapUrl,
  buildSocialValidatorLinks,
  buildSearchSubmissionLinks,
  defaultAdsConfig,
  defaultSeoConfig,
  type AdsConfig,
  type SeoConfig,
} from "@/lib/seoMonitor";

type SiteRow = {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  description: string | null;
  tagline: string | null;
  primary_language: string;
  seo_config: SeoConfig | null;
  monetization_config: { google_adsense?: AdsConfig["google_adsense"]; google_ad_manager?: AdsConfig["google_ad_manager"] } | null;
};

const db = supabase as any;

function siteUrl(site: SiteRow): string {
  if (site.domain) return `https://${site.domain.replace(/^https?:\/\//, "")}`;
  return `https://${site.slug}.delapresse.com`;
}

function scoreSite(seo: SeoConfig, ads: AdsConfig, site: SiteRow): { score: number; checks: { label: string; ok: boolean }[] } {
  const checks = [
    { label: "OG image set", ok: Boolean(seo.og_image_url || site.cover_image_url) },
    { label: "Site description filled", ok: Boolean(site.description && site.description.length >= 50) },
    { label: "Custom domain attached", ok: Boolean(site.domain) },
    { label: "Google site verification", ok: Boolean(seo.google_site_verification) },
    { label: "Sitemap submitted", ok: Boolean(seo.sitemap_submitted_at) },
    { label: "Twitter handle", ok: Boolean(seo.twitter_handle) },
    { label: "Ads publisher id", ok: Boolean(ads.google_adsense?.publisher_id) },
    { label: "At least one ad slot enabled", ok: Object.values(ads.google_adsense?.slots ?? {}).some(Boolean) },
  ];
  const passed = checks.filter((c) => c.ok).length;
  return { score: Math.round((passed / checks.length) * 100), checks };
}

export default function SEOMonitorPage() {
  const { t } = useTranslation();
  const { currentCompanyId, isPlatformOwner } = useCompany();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sites = useQuery({
    queryKey: ["seo-monitor-sites", currentCompanyId, isPlatformOwner],
    enabled: isPlatformOwner || !!currentCompanyId,
    queryFn: async () => {
      let q = db
        .from("websites")
        .select(
          "id, name, slug, domain, logo_url, cover_image_url, description, tagline, primary_language, seo_config, monetization_config"
        )
        .order("name");
      if (!isPlatformOwner && currentCompanyId) q = q.eq("company_id", currentCompanyId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SiteRow[];
    },
  });

  useEffect(() => {
    if (!selectedId && sites.data && sites.data.length > 0) {
      setSelectedId(sites.data[0].id);
    }
  }, [sites.data, selectedId]);

  const site = useMemo(
    () => sites.data?.find((s) => s.id === selectedId) ?? null,
    [sites.data, selectedId]
  );

  const seo: SeoConfig = useMemo(
    () => ({ ...defaultSeoConfig(), ...(site?.seo_config ?? {}) }),
    [site]
  );
  const ads: AdsConfig = useMemo(
    () => ({
      google_adsense: { ...defaultAdsConfig().google_adsense, ...(site?.monetization_config?.google_adsense ?? {}) },
      google_ad_manager: site?.monetization_config?.google_ad_manager,
    }),
    [site]
  );

  async function saveSeo(patch: Partial<SeoConfig>) {
    if (!site) return;
    const next = { ...seo, ...patch };
    const { error } = await db.from("websites").update({ seo_config: next }).eq("id", site.id);
    if (error) {
      toast({ variant: "destructive", description: error.message });
      return;
    }
    toast({ description: t("seoMonitor.toast.saved") });
    qc.invalidateQueries({ queryKey: ["seo-monitor-sites"] });
  }

  async function saveAds(patch: Partial<AdsConfig["google_adsense"]>) {
    if (!site) return;
    const nextAdsense = { ...(ads.google_adsense ?? defaultAdsConfig().google_adsense!), ...patch };
    const nextMonetization = {
      ...(site.monetization_config ?? {}),
      google_adsense: nextAdsense,
    };
    const { error } = await db.from("websites").update({ monetization_config: nextMonetization }).eq("id", site.id);
    if (error) {
      toast({ variant: "destructive", description: error.message });
      return;
    }
    toast({ description: t("seoMonitor.toast.saved") });
    qc.invalidateQueries({ queryKey: ["seo-monitor-sites"] });
  }

  async function toggleSlot(slot: AdSlotKey, on: boolean) {
    const slots = { ...(ads.google_adsense?.slots ?? {}), [slot]: on };
    await saveAds({ slots });
  }

  async function markSitemapSubmitted() {
    await saveSeo({ sitemap_submitted_at: new Date().toISOString() });
  }

  if (sites.isLoading) {
    return (
      <div className="container mx-auto py-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("featureGate.loading")}
      </div>
    );
  }

  if (!sites.data || sites.data.length === 0) {
    return (
      <div className="container mx-auto py-8 max-w-2xl space-y-4">
        <h1 className="text-2xl font-bold">{t("seoMonitor.title")}</h1>
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t("seoMonitor.empty.noSites")}{" "}
            <Link to="/websites/create" className="text-primary underline">
              {t("seoMonitor.empty.createSite")}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!site) return null;

  const url = siteUrl(site);
  const sitemapUrl = buildSitemapUrl(site.domain, site.slug);
  const ogImage = seo.og_image_url || site.cover_image_url || "";
  const { score, checks } = scoreSite(seo, ads, site);
  const validators = buildSocialValidatorLinks(url);
  const submissions = buildSearchSubmissionLinks(sitemapUrl, url);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div className="flex items-center gap-3">
          <Link to="/seo" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-lg font-semibold">{t("seoMonitor.title")}</h1>
            <p className="text-xs text-muted-foreground">{t("seoMonitor.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedId ?? undefined} onValueChange={setSelectedId}>
            <SelectTrigger className="w-[260px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sites.data.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="gap-2">
              <ExternalLink className="h-3.5 w-3.5" />
              {t("seoMonitor.openSite")}
            </Button>
          </a>
        </div>
      </header>

      <div className="p-6 space-y-6 animate-slide-in">
        {/* Score row */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="md:col-span-1">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("seoMonitor.score.label")}</p>
              <p className={`mt-2 text-4xl font-bold tabular-nums ${score >= 80 ? "text-emerald-500" : score >= 50 ? "text-amber-500" : "text-destructive"}`}>
                {score}
                <span className="text-base text-muted-foreground font-normal">/100</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{t("seoMonitor.score.hint")}</p>
            </CardContent>
          </Card>
          <Card className="md:col-span-3">
            <CardContent className="p-5 grid gap-2 sm:grid-cols-2">
              {checks.map((c) => (
                <div key={c.label} className="flex items-center gap-2 text-sm">
                  {c.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                  )}
                  <span className={c.ok ? "text-foreground" : "text-muted-foreground"}>{c.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="social" className="w-full">
          <TabsList>
            <TabsTrigger value="social">
              <ImageIcon className="h-4 w-4 mr-2" />
              {t("seoMonitor.tabs.social")}
            </TabsTrigger>
            <TabsTrigger value="verify">
              <Shield className="h-4 w-4 mr-2" />
              {t("seoMonitor.tabs.verify")}
            </TabsTrigger>
            <TabsTrigger value="sitemap">
              <MapPin className="h-4 w-4 mr-2" />
              {t("seoMonitor.tabs.sitemap")}
            </TabsTrigger>
            <TabsTrigger value="ads">
              <Megaphone className="h-4 w-4 mr-2" />
              {t("seoMonitor.tabs.ads")}
            </TabsTrigger>
          </TabsList>

          {/* Social sharing */}
          <TabsContent value="social" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" /> {t("seoMonitor.social.previewTitle")}
                </CardTitle>
                <CardDescription>{t("seoMonitor.social.previewHelp")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Card preview */}
                <div className="overflow-hidden rounded-lg border border-border bg-card max-w-xl">
                  <div className="aspect-[1200/630] bg-muted relative">
                    {ogImage ? (
                      <img src={ogImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                        1200 × 630 — {t("seoMonitor.social.noImage")}
                      </div>
                    )}
                  </div>
                  <div className="p-4 border-t border-border">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{(site.domain || `${site.slug}.delapresse.com`).toLowerCase()}</p>
                    <p className="mt-1 text-sm font-semibold text-foreground">{site.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{site.tagline || site.description || t("seoMonitor.social.noDescription")}</p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <FieldGroup
                    label={t("seoMonitor.social.ogImageLabel")}
                    help={t("seoMonitor.social.ogImageHelp")}
                  >
                    <Input
                      placeholder="https://cdn.../og-cover.png"
                      defaultValue={seo.og_image_url ?? ""}
                      onBlur={(e) => {
                        if ((e.target.value || null) !== (seo.og_image_url ?? null)) {
                          saveSeo({ og_image_url: e.target.value || null });
                        }
                      }}
                    />
                  </FieldGroup>
                  <FieldGroup
                    label={t("seoMonitor.social.twitterHandleLabel")}
                    help={t("seoMonitor.social.twitterHandleHelp")}
                  >
                    <Input
                      placeholder="@yoursite"
                      defaultValue={seo.twitter_handle ?? ""}
                      onBlur={(e) => {
                        if ((e.target.value || null) !== (seo.twitter_handle ?? null)) {
                          saveSeo({ twitter_handle: e.target.value || null });
                        }
                      }}
                    />
                  </FieldGroup>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("seoMonitor.social.validatorsTitle")}</CardTitle>
                <CardDescription>{t("seoMonitor.social.validatorsHelp")}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-2 sm:grid-cols-2">
                <ValidatorLink href={validators.facebook} icon={Facebook} label="Facebook Sharing Debugger" />
                <ValidatorLink href={validators.twitter} icon={Twitter} label="Twitter / X Card Validator" />
                <ValidatorLink href={validators.linkedin} icon={Linkedin} label="LinkedIn Post Inspector" />
                <ValidatorLink href={validators.openGraph} icon={Globe} label="OpenGraph.xyz preview" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification codes */}
          <TabsContent value="verify" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4" /> {t("seoMonitor.verify.title")}
                </CardTitle>
                <CardDescription>{t("seoMonitor.verify.help")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <VerificationField
                  label="Google Search Console"
                  placeholder="abcd1234..."
                  help={t("seoMonitor.verify.googleHelp")}
                  value={seo.google_site_verification ?? ""}
                  onSave={(v) => saveSeo({ google_site_verification: v || null })}
                  metaPreview={(v) => `<meta name="google-site-verification" content="${v}" />`}
                  consoleUrl="https://search.google.com/search-console/welcome"
                />
                <VerificationField
                  label="Bing Webmaster Tools"
                  placeholder="..."
                  help={t("seoMonitor.verify.bingHelp")}
                  value={seo.bing_site_verification ?? ""}
                  onSave={(v) => saveSeo({ bing_site_verification: v || null })}
                  metaPreview={(v) => `<meta name="msvalidate.01" content="${v}" />`}
                  consoleUrl="https://www.bing.com/webmasters"
                />
                <VerificationField
                  label="Yandex Webmaster"
                  placeholder="..."
                  help={t("seoMonitor.verify.yandexHelp")}
                  value={seo.yandex_site_verification ?? ""}
                  onSave={(v) => saveSeo({ yandex_site_verification: v || null })}
                  metaPreview={(v) => `<meta name="yandex-verification" content="${v}" />`}
                  consoleUrl="https://webmaster.yandex.com"
                />
                <VerificationField
                  label="Pinterest"
                  placeholder="..."
                  help={t("seoMonitor.verify.pinterestHelp")}
                  value={seo.pinterest_verification ?? ""}
                  onSave={(v) => saveSeo({ pinterest_verification: v || null })}
                  metaPreview={(v) => `<meta name="p:domain_verify" content="${v}" />`}
                  consoleUrl="https://www.pinterest.com/settings/claim"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sitemap */}
          <TabsContent value="sitemap" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> {t("seoMonitor.sitemap.title")}
                </CardTitle>
                <CardDescription>{t("seoMonitor.sitemap.help")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <FieldGroup label={t("seoMonitor.sitemap.sitemapUrl")}>
                  <div className="flex gap-2">
                    <Input readOnly value={sitemapUrl} />
                    <CopyButton text={sitemapUrl} />
                  </div>
                </FieldGroup>

                <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
                  <p className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    {t("seoMonitor.sitemap.howTitle")}
                  </p>
                  <ol className="list-decimal pl-5 space-y-1 text-muted-foreground text-xs">
                    <li>{t("seoMonitor.sitemap.step1")}</li>
                    <li>{t("seoMonitor.sitemap.step2")}</li>
                    <li>{t("seoMonitor.sitemap.step3")}</li>
                  </ol>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  <ValidatorLink href={submissions.googleSearchConsole} icon={Search} label="Google Search Console" />
                  <ValidatorLink href={submissions.bingWebmaster} icon={Search} label="Bing Webmaster Tools" />
                  <ValidatorLink href={submissions.googleSitemapPing} icon={Globe} label={t("seoMonitor.sitemap.googlePing")} />
                  <ValidatorLink href={submissions.indexNow} icon={Globe} label="IndexNow" />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-border bg-card p-3">
                  <div className="text-sm">
                    <p className="font-medium text-foreground">{t("seoMonitor.sitemap.markSubmitted")}</p>
                    <p className="text-xs text-muted-foreground">
                      {seo.sitemap_submitted_at
                        ? t("seoMonitor.sitemap.submittedOn", { date: new Date(seo.sitemap_submitted_at).toLocaleDateString() })
                        : t("seoMonitor.sitemap.notSubmitted")}
                    </p>
                  </div>
                  <Button size="sm" variant={seo.sitemap_submitted_at ? "outline" : "default"} onClick={markSitemapSubmitted}>
                    {seo.sitemap_submitted_at ? t("seoMonitor.sitemap.refreshMark") : t("seoMonitor.sitemap.markDone")}
                  </Button>
                </div>

                <FieldGroup
                  label={t("seoMonitor.sitemap.robotsExtraLabel")}
                  help={t("seoMonitor.sitemap.robotsExtraHelp")}
                >
                  <Textarea
                    rows={4}
                    placeholder="User-agent: GPTBot\nDisallow: /"
                    defaultValue={seo.robots_txt_extra ?? ""}
                    onBlur={(e) => {
                      if ((e.target.value || null) !== (seo.robots_txt_extra ?? null)) {
                        saveSeo({ robots_txt_extra: e.target.value || null });
                      }
                    }}
                  />
                </FieldGroup>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ads */}
          <TabsContent value="ads" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Megaphone className="h-4 w-4" /> {t("seoMonitor.ads.adsenseTitle")}
                </CardTitle>
                <CardDescription>{t("seoMonitor.ads.adsenseHelp")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <FieldGroup
                  label={t("seoMonitor.ads.publisherIdLabel")}
                  help={t("seoMonitor.ads.publisherIdHelp")}
                >
                  <Input
                    placeholder="ca-pub-1234567890123456"
                    defaultValue={ads.google_adsense?.publisher_id ?? ""}
                    onBlur={(e) => {
                      const v = e.target.value.trim() || null;
                      if (v !== (ads.google_adsense?.publisher_id ?? null)) {
                        saveAds({ publisher_id: v });
                      }
                    }}
                  />
                </FieldGroup>

                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Info className="h-4 w-4" /> {t("seoMonitor.ads.snippetTitle")}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("seoMonitor.ads.snippetHelp")}</p>
                  {ads.google_adsense?.publisher_id ? (
                    <SnippetBlock value={`<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ads.google_adsense.publisher_id}" crossorigin="anonymous"></script>`} />
                  ) : (
                    <p className="text-xs text-muted-foreground italic">{t("seoMonitor.ads.snippetEmpty")}</p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t("seoMonitor.ads.autoAds")}</p>
                    <p className="text-xs text-muted-foreground">{t("seoMonitor.ads.autoAdsHelp")}</p>
                  </div>
                  <Switch
                    checked={Boolean(ads.google_adsense?.auto_ads)}
                    onCheckedChange={(v) => saveAds({ auto_ads: v })}
                  />
                </div>

                <FieldGroup
                  label="ads.txt"
                  help={t("seoMonitor.ads.adsTxtHelp")}
                >
                  <Textarea
                    rows={3}
                    placeholder="google.com, pub-1234567890123456, DIRECT, f08c47fec0942fa0"
                    defaultValue={ads.google_adsense?.ads_txt ?? ""}
                    onBlur={(e) => {
                      const v = e.target.value || null;
                      if (v !== (ads.google_adsense?.ads_txt ?? null)) {
                        saveAds({ ads_txt: v });
                      }
                    }}
                  />
                </FieldGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("seoMonitor.ads.slotsTitle")}</CardTitle>
                <CardDescription>{t("seoMonitor.ads.slotsHelp")}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                {AD_SLOTS.map((slot) => {
                  const enabled = Boolean(ads.google_adsense?.slots?.[slot.key]);
                  return (
                    <div
                      key={slot.key}
                      className={`rounded-lg border p-4 transition-colors ${enabled ? "border-primary/50 bg-primary/5" : "border-border bg-card"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{t(slot.labelKey)}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{t(slot.helpKey)}</p>
                          <Badge variant="outline" className="mt-2 text-[10px]">{slot.position}</Badge>
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={(v) => toggleSlot(slot.key, v)}
                          disabled={!ads.google_adsense?.publisher_id}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {!ads.google_adsense?.publisher_id && (
              <Card>
                <CardContent className="py-4 text-xs text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  {t("seoMonitor.ads.publisherRequired")}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function FieldGroup({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</Label>
        {help && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{help}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  );
}

function ValidatorLink({ href, icon: Icon, label }: { href: string; icon: React.ElementType; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm hover:border-primary/50 hover:bg-accent/30 transition-colors"
    >
      <span className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-foreground">{label}</span>
      </span>
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
    </a>
  );
}

function CopyButton({ text }: { text: string }) {
  const { toast } = useToast();
  const { t } = useTranslation();
  return (
    <Button
      size="icon"
      variant="outline"
      onClick={() => {
        navigator.clipboard.writeText(text);
        toast({ description: t("seoMonitor.toast.copied") });
      }}
      aria-label="Copy"
    >
      <Copy className="h-4 w-4" />
    </Button>
  );
}

function SnippetBlock({ value }: { value: string }) {
  return (
    <div className="flex gap-2 items-start">
      <pre className="flex-1 overflow-x-auto rounded-md bg-background border border-border p-3 text-[11px] font-mono leading-relaxed text-foreground">
        <code>{value}</code>
      </pre>
      <CopyButton text={value} />
    </div>
  );
}

function VerificationField({
  label,
  placeholder,
  help,
  value,
  onSave,
  metaPreview,
  consoleUrl,
}: {
  label: string;
  placeholder: string;
  help: string;
  value: string;
  onSave: (v: string) => void;
  metaPreview: (v: string) => string;
  consoleUrl: string;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold">{label}</p>
          {value && <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-500">{t("seoMonitor.verify.linked")}</Badge>}
        </div>
        <a href={consoleUrl} target="_blank" rel="noopener noreferrer">
          <Button size="sm" variant="ghost" className="gap-1.5">
            <ExternalLink className="h-3 w-3" />
            {t("seoMonitor.verify.openConsole")}
          </Button>
        </a>
      </div>
      <p className="text-xs text-muted-foreground">{help}</p>
      <div className="flex gap-2">
        <Input
          placeholder={placeholder}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            if (local !== value) onSave(local.trim());
          }}
        />
      </div>
      {local && <SnippetBlock value={metaPreview(local.trim())} />}
    </div>
  );
}
